/**
 * Recently viewed cities in the header menus.
 * ---------------------------------------------------------------------------
 *
 * The CUBE / FRAME / GRAND / BLOK rows in the mega-menu and the mobile sidebar
 * are merchandised server-side out of `featured_cities_by_country` — the top
 * cities for the visitor's country. That list is fixed at render time, and
 * Liquid has no way of knowing which city the visitor just looked at (there is
 * no cookie access, and the city is usually not in the featured set anyway —
 * Aachen never outranks Berlin).
 *
 * So the shift happens here instead. Two halves:
 *
 *   record()  The product pages emit a small JSON blob for the ONE variant they
 *             render (all four configurators resolve `?variant=` server-side,
 *             so a page view is a city view). GRAND is the exception — its city
 *             `<select>` swaps in place without navigating, so it calls
 *             record() directly on change.
 *
 *   inject()  Every page: prepend a card per recent city to each marked row,
 *             newest first, and drop as many curated cards off the end. Cards
 *             are CLONED from the row's own first card rather than built from a
 *             template string, so each context keeps its exact markup — the
 *             desktop grid's `h6 text-center` label, the sidebar's `smallcaps`
 *             one, GRAND's -26% crop — without any of it being duplicated here.
 *
 * Entries expire after 30 days: long enough that a visitor returning next week
 * still finds the city they were looking at, short enough that a link to a
 * variant that has since been pulled does not linger forever.
 */
(() => {
  const KEY = 'cf:recent-cities';

  /**
   * How many recent cities a MERGED row needs before it shows any.
   *
   * A merged row holds the visitor's cities and the merchandised ones together under
   * one heading, and that heading names whichever of the two you are looking at. With
   * a single recent city it said "Recently viewed" over a row that was seven eighths
   * merchandised, which reads as a mistake rather than as a shortcut. Below three,
   * nothing is hoisted and the row is exactly what it says it is.
   *
   * The dedicated rows have no such problem - they are a row of their own, correct at
   * any count - so this does not apply to them.
   */
  const MERGED_MINIMUM = 3;

  /**
   * How many cities the recent row needs before it OPENS the panel rather than
   * merely appearing in it.
   *
   * The tab shows up with the first city — someone who has looked at one should
   * be able to get back to it. But leading with a row of one, against eight
   * merchandised cities on the tab beside it, spends the panel's first screen on
   * the single thing the visitor least needs help finding: they were just there.
   * At three the row is a short history rather than a souvenir, and worth opening
   * on.
   *
   * Same number as MERGED_MINIMUM, for the same reason, and deliberately not
   * shared with it: one governs whether a curated row may be relabelled, the
   * other which tab is selected, and they are free to move apart.
   */
  const LEAD_MINIMUM = 3;

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Catalogue cities keep for a month. Clearance passes its own, shorter, value
  // — those items are a unit or two deep and an entry that outlives its stock
  // would leave a sold-out page sitting in a merchandising slot.
  const DEFAULT_TTL_DAYS = 30;

  // Rows show at most 10 cards (desktop CUBE), so anything past that can never
  // be displayed. A little headroom absorbs entries that dedupe against the
  // curated cards.
  const MAX_PER_BRAND = 16;

  // 4:5 CHANGEOVER — remove after 2026-11-18 18:00 UTC, when the last entry recorded
  // before it has expired. The CUBE images were replaced in place on 2026-09-30: square
  // renders became 4:5 combos under the same file names. An entry recorded before
  // carries the old ?v=, and the CDN still holds the square under that exact URL,
  // so Recently viewed drew the old picture — smaller in the card, lower-set —
  // beside Popular's new one. Such an entry is sent with a ?v= from after the swap
  // instead, which the CDN has never cached, so it fetches the file as it is now.
  // Moved past each later in-place swap, which re-stamps every older entry to a new,
  // uncached URL: the re-rendered combos of 2026-10-01 (done by 05:00 UTC), last
  // Macao's re-render of 2026-10-16 (swapped by 13:10 UTC). The GRAND city layers
  // and previews were replaced in place the same way on 2026-10-17 (4096 px
  // re-renders, swapped by 13:00 UTC; Hamburg went from on-request to regular), and Miami's and Seoul's on
  // 2026-10-19 (swapped by 17:13 UTC).
  const IMAGES_REPLACED = { cube: Date.UTC(2026, 9, 16, 13, 30), grand: Date.UTC(2026, 9, 19, 18, 0) };

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /**
   * A row holds one card per CITY, never one per area — the curated loop takes
   * `city.cube_variant | first`, so Berlin is one card even though five Berlin
   * areas exist. Matching on the variant id alone would break that: view
   * "Berlin - Museum Island" (a clearance area, or just another area of the
   * regular product) while Berlin is merchandised and the row ends up with two
   * Berlin cards, same label, different picture. Dedupe on the city name, which
   * every side takes from the same metaobject field.
   */
  function cityKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  /** { cube: [{ id, h: handle, c: city, a: area, i: image url, t: timestamp, ttl }], ... }, newest first. */
  function read() {
    let raw;

    try {
      raw = JSON.parse(localStorage.getItem(KEY) || '{}');
    } catch (e) {
      return {}; // Corrupt payload, or Safari private mode
    }

    const now = Date.now();
    const store = {};

    for (const brand of Object.keys(raw)) {
      if (!Array.isArray(raw[brand])) continue;
      store[brand] = raw[brand]
        .filter((entry) => {
          if (!entry || !entry.id || !entry.c) return false;
          return now - entry.t < (entry.ttl || DEFAULT_TTL_DAYS) * DAY_MS;
        })
        .slice(0, MAX_PER_BRAND);

      const replaced = IMAGES_REPLACED[brand];
      if (replaced) {
        store[brand].forEach((entry) => {
          if (entry.i && entry.t < replaced) {
            entry.i = entry.i.replace(/([?&]v=)\d+/, '$1' + Math.floor(replaced / 1000));
          }
        });
      }
    }

    return store;
  }

  /**
   * The list is kept only with the visitor's "Preferences" consent (2026-10-17, Alex - the welcome screen's cookie
   * settings call it "remembers what you looked at"). Shopify's Customer Privacy API answers: where its consent regions
   * ask for consent (the EU, the UK ...) that is after the visitor allowed it, elsewhere unless they refused. Without
   * the API there is no answer, and nothing is kept. A refusal also empties what was kept.
   */
  function withPreferences(then) {
    const answer = () => {
      try {
        return window.Shopify.customerPrivacy.preferencesProcessingAllowed() === true;
      } catch (e) {
        return false;
      }
    };
    if (window.Shopify && window.Shopify.customerPrivacy) return then(answer());
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return then(false);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => then(error ? false : answer()));
  }

  function forget() {
    try {
      localStorage.removeItem(KEY);
    } catch (e) {
      // storage blocked: nothing was kept
    }
  }

  document.addEventListener('visitorConsentCollected', (event) => {
    if (event.detail && event.detail.preferencesAllowed === false) forget();
  });

  function record(entry) {
    if (!entry || !entry.brand || !entry.id || !entry.city || !entry.handle) return;
    withPreferences((allowed) => (allowed ? keep(entry) : forget()));
  }

  function keep(entry) {
    const store = read();
    const id = String(entry.id);

    // Re-viewing a city moves it back to the front rather than duplicating it.
    // Matching on the city too, so the clearance Berlin and the catalogue Berlin
    // never both sit in the list waiting for the row to drop one of them.
    const list = (store[entry.brand] || []).filter(
      (item) => String(item.id) !== id && cityKey(item.c) !== cityKey(entry.city)
    );

    const next = { id: id, h: entry.handle, c: entry.city, a: entry.area || '', i: entry.image || '', t: Date.now() };
    if (entry.ttlDays) next.ttl = entry.ttlDays;

    list.unshift(next);
    store[entry.brand] = list.slice(0, MAX_PER_BRAND);

    try {
      localStorage.setItem(KEY, JSON.stringify(store));
    } catch (e) {
      // Safari in private mode does not allow setting item, we silently fail
    }
  }

  /**
   * Swap the `width=` of a CDN image URL. The stored URL is emitted at width
   * 600; the row needs whatever widths its own srcset already asks for.
   */
  function sizedUrl(url, width) {
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function buildCard(template, entry, row) {
    const card = template.cloneNode(true);

    card.href = root + 'products/' + entry.h + '?variant=' + entry.id;
    card.dataset.cfVariant = entry.id;
    card.dataset.cfCity = entry.c;
    card.dataset.cfRecent = 'true';

    // Which list the entry came from, for a row that reads more than one ("cube trio"): the CSS can give a kind its
    // own card, and the row can say what size that kind's picture is drawn at (data-cf-recent-sizes-<kind>). No row
    // reads more than one list since the Desk panel's desktop row went (2026-10-06).
    if (entry.kind) card.dataset.cfKind = entry.kind;
    const kindSizes = entry.kind && row && row.dataset['cfRecentSizes' + entry.kind[0].toUpperCase() + entry.kind.slice(1)];

    const image = card.querySelector('img');

    if (image) {
      const widths = (image.getAttribute('srcset') || '').match(/\d+(?=w)/g);
      const srcWidth = (image.getAttribute('src') || '').match(/[?&]width=(\d+)/);

      // The curated cards carry the media's full width in `src` and let the
      // srcset do the real work. No reason to repeat that here — the largest
      // srcset candidate is the widest size the row can ever ask for.
      image.setAttribute(
        'src',
        sizedUrl(entry.i, widths ? widths[widths.length - 1] : srcWidth ? srcWidth[1] : 400)
      );

      if (widths) {
        image.setAttribute(
          'srcset',
          widths.map((width) => sizedUrl(entry.i, width) + ' ' + width + 'w').join(', ')
        );
      } else {
        image.removeAttribute('srcset');
      }

      if (kindSizes) image.setAttribute('sizes', kindSizes);

      image.setAttribute('alt', entry.c);
      image.setAttribute('loading', 'lazy');

      // The intrinsic size came from the card we cloned and does not describe
      // this image. The rows size every card by CSS aspect-ratio anyway.
      image.removeAttribute('width');
      image.removeAttribute('height');
    }

    // The name, and the area under it where the row has a slot for one. Both are
    // addressed by their marker rather than by position: the desktop card carries
    // two <p>s and `querySelector('p')` would fill whichever came first, while the
    // sidebar rows have only the one and no marker at all.
    const label = card.querySelector('[data-cf-city-label]') || card.querySelector('p');
    const area = card.querySelector('[data-cf-city-area]');

    // A Trio is recorded as "Antique Trio" with the area "Trio", which says it twice once the area stands under the
    // name: it reads "Antique" over "Trio", the way a CUBE reads "Paris" over "Eiffel Tower". Trios only, and only
    // where the card has an area line - a CUBE named for its area ("Oklahoma City" over "City") keeps its full name.
    let name = entry.c;
    if (entry.kind === 'trio' && area && entry.a && name.endsWith(' ' + entry.a)) {
      name = name.slice(0, -(entry.a.length + 1));
    }
    if (label) label.textContent = name;

    // Emptied rather than left alone when this city has no area. The clone source
    // is a real city, so its area would otherwise stand under someone else's name.
    if (area) area.textContent = entry.a || '';

    return card;
  }

  /**
   * A row of its own, holding nothing but recent cities.
   *
   * The merged behaviour below rewrites a curated row in place and relabels it,
   * which suits a panel with room for one row. Where a panel shows "Popular"
   * and "Recently viewed" as two separate rows, the popular one carries no
   * marker at all and is left alone, and this fills the second from an empty
   * shell: one card is rendered as a clone source and the block around it ships
   * `hidden`, so a first-time visitor sees one row rather than a gap-toothed
   * second one.
   */
  function injectDedicated(row, store) {
    // A row may read more than one list ("cube trio"), merged newest first. Each entry keeps the name of its list.
    const entries = row.dataset.cfRecentRow
      .split(/\s+/)
      .flatMap((kind) => (store[kind] || []).map((entry) => Object.assign({}, entry, { kind: kind })))
      .sort((a, b) => b.t - a.t);
    const template = row.querySelector(':scope > [data-cf-card]');
    if (!template) return;

    const limit = Number(row.dataset.cfRecentLimit) || 6;
    const seen = new Set();
    const fresh = [];

    for (const entry of entries) {
      if (!entry.i) continue;
      if (seen.has(cityKey(entry.c))) continue;
      seen.add(cityKey(entry.c));
      fresh.push(buildCard(template, entry, row));
      if (fresh.length >= limit) break;
    }

    // Nothing to show: the block stays hidden and the row never appears.
    if (fresh.length === 0) return;

    fresh.forEach((card) => row.insertBefore(card, template));
    template.remove();

    // Where the curated cities stand beside this row (the Desk panel: data-cf-recent-dedupe on the element around
    // both), a curated city this row now shows is hidden there, so the same city is not shown twice side by side.
    const dedupe = row.closest('[data-cf-recent-dedupe]');
    if (dedupe) {
      dedupe.querySelectorAll('[data-cf-card]:not([data-cf-recent])').forEach((card) => {
        if (seen.has(cityKey(card.dataset.cfCity))) card.hidden = true;
      });
    }

    const block = row.closest('[data-cf-recent-block]');
    if (!block) return;

    block.hidden = false;

    // The mobile drawer puts the two rows behind tabs. The Recently viewed tab
    // ships hidden and is revealed here rather than on load, so a first-time
    // visitor never sees a tab strip with a dead half.
    const panel = block.closest('[data-cf-tab-panel]') || block;
    const scope = panel.parentElement;
    const tabs = scope && scope.querySelector('[data-cf-tabs]');
    const name = panel.dataset.cfTabPanel;

    if (tabs && name) {
      const tab = tabs.querySelector(`[data-cf-tab="${name}"]`);
      tabs.classList.add('is-multi');

      if (tab) {
        // The tab appears with the first city. Whether it is also SELECTED is a
        // separate question, answered by LEAD_MINIMUM.
        tab.hidden = false;

        // Below the threshold the panel opens where the markup left it — Popular
        // — and this only makes the rows agree with that, because unhiding the
        // block above has just revealed a second row behind the first.
        const leads = fresh.length >= LEAD_MINIMUM;
        const marked = tabs.querySelector('[data-cf-tab][aria-current="true"]');
        const selected = leads ? name : (marked && marked.dataset.cfTab) || name;

        tabs.querySelectorAll('[data-cf-tab]').forEach((button) => {
          button.setAttribute('aria-current', button.dataset.cfTab === selected ? 'true' : 'false');
        });
        scope.querySelectorAll(':scope > [data-cf-tab-panel]').forEach((item) => {
          item.hidden = item.dataset.cfTabPanel !== selected;
        });
      }
    }
  }

  function injectRow(row, store) {
    if (row.dataset.cfRecentMode === 'only') {
      injectDedicated(row, store);
      return;
    }

    const entries = store[row.dataset.cfRecentRow];
    if (!entries || entries.length < MERGED_MINIMUM) return;

    const cards = Array.from(row.querySelectorAll(':scope > [data-cf-card]'));
    if (cards.length === 0) return;

    // Hold the insertion point before any removal — once every curated card has
    // deduped away there is nothing left to insert in front of, and the sidebar
    // rows end with a "more available" link that must stay last.
    const marker = document.createComment('recently viewed');
    row.insertBefore(marker, cards[0]);

    const limit = cards.length;
    const seen = new Set();
    const fresh = [];

    for (const entry of entries) {
      // No image means the clone would keep the template card's picture under
      // someone else's city name. Better to leave the row alone.
      if (!entry.i) continue;
      if (seen.has(cityKey(entry.c))) continue;
      seen.add(cityKey(entry.c));
      fresh.push(buildCard(cards[0], entry));
      if (fresh.length >= limit) break;
    }

    // A recent city that is also merchandised moves to the front rather than
    // appearing twice.
    cards.forEach((card) => {
      if (seen.has(cityKey(card.dataset.cfCity))) card.remove();
    });

    // Every entry skipped (all imageless): leave the row, and its heading, alone.
    if (fresh.length === 0) {
      marker.remove();
      return;
    }

    fresh.forEach((card) => row.insertBefore(card, marker));
    marker.remove();

    const kept = Array.from(row.querySelectorAll(':scope > [data-cf-card]'));
    kept.slice(limit).forEach((card) => card.remove());

    // "Popular cities" stops being true the moment the visitor's own city leads the row.
    // The replacement copy is translated in Liquid and parked on the heading — this side
    // only picks between two strings it was handed. Still used by the FRAME, GRAND and
    // BLOK panels, which have one heading over one row.
    const heading = row.parentElement && row.parentElement.querySelector('[data-cf-recent-heading]');
    if (heading && heading.dataset.cfRecentHeading) {
      heading.textContent = heading.dataset.cfRecentHeading;
    }
  }

  /**
   * Tab switching for the drawer's two city rows, delegated once.
   *
   * Mirrors `featured-collections`: `aria-current` on the buttons, the matching
   * panel shown and the rest hidden. Nothing is destroyed, so switching back and
   * forth costs nothing and the recent cards keep their event-free markup.
   */
  function bindTabs() {
    document.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-cf-tab]');
      if (!tab) return;

      const tabs = tab.closest('[data-cf-tabs]');
      const scope = tabs && tabs.parentElement;
      if (!scope) return;

      tabs.querySelectorAll('[data-cf-tab]').forEach((button) => {
        button.setAttribute('aria-current', button === tab ? 'true' : 'false');
      });

      scope.querySelectorAll(':scope > [data-cf-tab-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.cfTabPanel !== tab.dataset.cfTab;
      });
    });
  }

  function inject() {
    const rows = document.querySelectorAll('[data-cf-recent-row]');
    if (rows.length === 0) return;

    const store = read();
    rows.forEach((row) => injectRow(row, store));
  }

  bindTabs();

  /**
   * The CUBE clearance product swaps city in place through the standard variant
   * picker, which rewrites `?variant=` and fires `variant:change` on the form.
   * The event payload carries the variant title ("Berlin - Museum Island"), not
   * the metaobject name the rows dedupe on, so the city and image come from a
   * map Liquid rendered instead. Sold-out variants are absent from that map.
   */
  function watchVariantPicker() {
    const node = document.querySelector('script[data-cf-recent-city-map]');
    if (!node) return;

    let map;

    try {
      map = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }

    if (!map || !map.variants) return;

    document.addEventListener('variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      const match = variant && map.variants[String(variant.id)];
      if (!match) return;

      record({
        brand: map.brand,
        id: variant.id,
        handle: map.handle,
        city: match.city,
        area: match.area,
        image: match.image,
        ttlDays: map.ttlDays
      });
    });
  }

  window.CFRecentCities = { record: record, read: read };

  function start() {
    // Record before injecting, so the city on screen leads its own row.
    const blob = document.querySelector('script[data-cf-recent-city]');

    if (blob) {
      try {
        record(JSON.parse(blob.textContent));
      } catch (e) {
        // Malformed blob must not take the menus down with it
      }
    }

    watchVariantPicker();

    // A task late, not a frame: GRAND records its city from its own
    // DOMContentLoaded handler (the selection lives in a `<select>`, not in the
    // URL) and that handler is registered after this one, so injecting inline
    // would miss it. requestAnimationFrame would be the natural choice but it
    // never fires in a background tab, and a menu built on the next visit to
    // that tab still has to be right. The menus are closed either way.
    setTimeout(inject, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
